import type { ReactNode, RefObject } from 'react';

import { GUTTER_CLASSES, PULL_GUTTER_CLASSES } from 'components/Layout';
import { cn } from 'utils/classNames';

type SwipeWrapperSpacing = 0.5 | 1 | 1.5;

interface Props {
	/** Classes for the container in active state. */
	activeClassName?: string;

	/** React children */
	children?: ReactNode;

	/** Extra class names for the container, for both active and inactive state. */
	className?: string;

	/** Classes for the container in inactive state. */
	inactiveClassName?: string;

	/** Pull the wrapper over horizontal gutters to the edge of the screen */
	pullGutters?: boolean;

	ref?: RefObject<HTMLUListElement | null>;

	/** Spacing between child components */
	spacing?: SwipeWrapperSpacing;
}

/**
 * A wrapper that can be used to list components on one row and allow users
 * to swipe/scroll horizontally.
 *
 * Pass an inactive class name to render an additional container without any
 * scrolling classes.
 */

export default function SwipeWrapper({
	activeClassName,
	children,
	className,
	inactiveClassName,
	pullGutters = false,
	ref,
	spacing = 0.5,
}: Props) {
	return (
		// The ref forwarding to just the main container is kind of broken and bad
		// but is enough for current usage. This entire component will have to be
		// considered anyway for accessibility so it's hopefully fairly short lived.
		<>
			<ul
				ref={ref}
				className={cn(
					className,
					activeClassName,
					'flex overflow-x-scroll pt-0.5 pb-2 whitespace-nowrap scrolling-touch',
					spacing === 0.5 && 'gap-2',
					spacing === 1 && 'gap-4',
					spacing === 1.5 && 'gap-6',
					pullGutters && [PULL_GUTTER_CLASSES, GUTTER_CLASSES],
				)}
			>
				{children}
			</ul>
			{inactiveClassName && (
				<ul className={cn(className, inactiveClassName)}>{children}</ul>
			)}
		</>
	);
}
SwipeWrapper.displayName = 'SwipeWrapper';
